import type { SupportedLanguage, Translations } from '@pbk-web/types';
import { FormType } from '@pbk-web/types';
import { LanguageUtils, StringUtils, toRecord } from '@pbk-web/utils';

import { type FormBuilderPlaceholder, PlaceholderType } from '../components/form-builder/FormBuilderTypes';
import i18n from '../i18n';
import type { ClientForm } from '../models/ClientForm';
import type { DocumentListItem } from '../models/Document';
import type { ActionPlaceholderData, FormListItem } from '../models/Form';

type FormLite = {
  subtitle?: string;
  form: {
    translations: Translations;
  };
};

type DocumentLite = {
  subtitle?: string;
  documentNumber: number;
  templateModuleTranslations: Translations;
  translations?: Translations;
  form?: {
    translations: Translations;
  };
};

export default class FormUtils {
  public static getFormTitle(formLite: FormLite, lang: SupportedLanguage | null = null): string {
    const language: SupportedLanguage = lang || i18n.language;
    const translations = formLite.form?.translations ? formLite.form.translations[language] || {} : {};
    return formLite.subtitle || translations.title || '';
  }

  public static getDocumentTitle(document: DocumentLite) {
    return `${StringUtils.makePrefixWithNumber(undefined, document.documentNumber, document.templateModuleTranslations)}-${
      document.subtitle ||
      LanguageUtils.getTranslation('title', 'translations' in document ? document.translations || {} : document.form?.translations || {})
    }`;
  }

  public static getAttachmentTitle(fileName: string, sortIndex: number, clientForm?: ClientForm | DocumentListItem | FormListItem): string {
    const isDocumentForm =
      !!clientForm && clientForm.type === FormType.Document && 'documentNumber' in clientForm && 'templateModuleTranslations' in clientForm;

    if (isDocumentForm) {
      const document = clientForm as DocumentListItem;

      const prefix = StringUtils.makePrefixWithNumber(undefined, document.documentNumber, document.templateModuleTranslations);

      const version = `v${document.majorVersion}`;
      const index = StringUtils.addLeadingZeros(sortIndex + 1);

      return `${prefix}-${version}-${index}-${fileName}`;
    }

    return fileName;
  }

  public static getFormTemplateTitle(form: FormLite, lang: SupportedLanguage | null = null): string {
    const language: SupportedLanguage = lang || i18n.language;
    const translations = form.form?.translations ? form.form.translations[language] || {} : {};
    return translations.title || '';
  }

  public static formHostName = (formClient: { clientId: string; clientName: string }, clientId: string, format?: string): string => {
    if (formClient.clientId !== clientId) {
      return format ? format.replace('{client}', formClient.clientName) : `${formClient.clientName}`;
    }
    return '';
  };

  public static formPlaceholdersToActionPlaceholderData = (placeholders?: FormBuilderPlaceholder[]) => {
    return placeholders
      ? toRecord(
          placeholders.map(
            (x) =>
              ({
                placeholder: x.placeholder,
                actionType: 'DateAction', // value doesn't matter, but need to provide it to satisfy type
                friendlyName: x.friendlyName,
                dataFormat: x.dataFormat,
                answers: [],
                clientFormIds: [],
                actionIds: [],
                type: x.type ?? PlaceholderType.Question,
                fallbackValue: x.fallbackValue,
              }) as ActionPlaceholderData & { placeholder: string },
          ),
          'placeholder',
        )
      : {};
  };

  public static actionPlaceholderDataToFormPlaceholders = (data?: Record<string, ActionPlaceholderData>): FormBuilderPlaceholder[] => {
    if (!data) return [];

    return Object.entries(data).map(([placeholder, x]) => ({
      placeholder,
      friendlyName: x.friendlyName,
      dataFormat: x.dataFormat,
      type: x.type ?? PlaceholderType.Question,
      actionIdContainingAssociation: null,
      fallbackValue: x.fallbackValue,
    }));
  };
}
